import { useState, useEffect } from 'react'
import { PageHeader, Breadcrumb } from '@/components/layout'
import {
  Card,
  CardContent,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Alert,
} from '@/components/ui'
import { LogIn, Shield, AppWindow, Key, Download, RefreshCw, User, CreditCard, Bell } from 'lucide-react'
import { useApiClient, api, type ActivityItem } from '@/api'

interface GroupedActivity {
  date: string
  displayDate: string
  events: ActivityItem[]
}

export function ActivityPage() {
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [filter, setFilter] = useState('all')
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const loadData = async (append = false) => {
    if (!append) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }
    setError(null)
    try {
      const currentCount = append ? activity.length : 0
      const response = await api.getActivity(client, 50)
      const newItems = response.items

      if (append) {
        setActivity((prev) => [...prev, ...newItems])
      } else {
        setActivity(newItems)
      }

      // If we got fewer than requested, no more to load
      setHasMore(newItems.length >= 50)
    } catch (err) {
      console.error('Failed to load activity:', err)
      setError('Failed to load activity')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleExport = async (format: 'json' | 'csv') => {
    setIsExporting(true)
    try {
      const response = await fetch("/api/me/activity/export?format=" + format, {
        credentials: 'include',
      })
      if (!response.ok) throw new Error('Export failed')
      
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = "activity-export." + format
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export activity:', err)
      setError('Failed to export activity')
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Filter activity
  const filteredActivity = activity.filter((item) => {
    if (filter === 'all') return true
    const action = item.action.toLowerCase()
    if (filter === 'login') return action.includes('login') || action.includes('session')
    if (filter === 'security') return action.includes('mfa') || action.includes('password') || action.includes('security')
    if (filter === 'apps') return action.includes('app') || action.includes('authorize') || action.includes('revoke')
    return true
  })

  // Group activity by date
  const groupedActivity = groupActivityByDate(filteredActivity)

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Activity"
          description="Your account activity log"
          breadcrumb={
            <Breadcrumb
              items={[
                { label: 'Account', href: '/account' },
                { label: 'Activity' },
              ]}
            />
          }
        />
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error && activity.length === 0) {
    return (
      <div>
        <PageHeader
          title="Activity"
          description="Your account activity log"
          breadcrumb={
            <Breadcrumb
              items={[
                { label: 'Account', href: '/account' },
                { label: 'Activity' },
              ]}
            />
          }
        />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{error}</p>
            <Button onClick={() => loadData()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Activity"
        description="Your account activity log"
        breadcrumb={
          <Breadcrumb
            items={[
              { label: 'Account', href: '/account' },
              { label: 'Activity' },
            ]}
          />
        }
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleExport('csv')}
            loading={isExporting}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        }
      />

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="login">Logins</SelectItem>
            <SelectItem value="security">Security</SelectItem>
            <SelectItem value="apps">Apps</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Activity list */}
      <div className="space-y-6">
        {groupedActivity.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-[var(--text-secondary)]">
                {filter === 'all' ? 'No activity yet' : 'No matching activity'}
              </p>
            </CardContent>
          </Card>
        ) : (
          groupedActivity.map((group) => (
            <div key={group.date}>
              <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-3">
                {group.displayDate}
              </h3>
              <Card>
                <CardContent className="divide-y divide-[var(--border-subtle)]">
                  {group.events.map((event, index) => (
                    <div
                      key={event.id}
                      className={`flex items-start gap-3 py-4 ${
                        index === 0 ? 'pt-0' : ''
                      } ${index === group.events.length - 1 ? 'pb-0' : ''}`}
                    >
                      <div className="rounded-full bg-[var(--bg-elevated-2)] p-2">
                        <ActivityIcon action={event.action} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--text-primary)]">
                          {formatActionTitle(event.action)}
                        </p>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {event.description}
                        </p>
                        {(event.ip_address || event.user_agent) && (
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            {[event.user_agent, event.ip_address].filter(Boolean).join(' • ')}
                          </p>
                        )}
                      </div>
                      <p className="text-sm text-[var(--text-muted)]">
                        {formatTime(event.created_at)}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          ))
        )}
      </div>

      {/* Load more */}
      {hasMore && filteredActivity.length > 0 && (
        <div className="mt-6 text-center">
          <Button
            variant="secondary"
            onClick={() => loadData(true)}
            loading={loadingMore}
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}

function ActivityIcon({ action }: { action: string }) {
  const iconClass = 'h-4 w-4 text-[var(--text-secondary)]'
  const actionLower = action.toLowerCase()

  if (actionLower.includes('login') || actionLower.includes('session')) {
    return <LogIn className={iconClass} />
  }
  if (actionLower.includes('app') || actionLower.includes('authorize') || actionLower.includes('revoke')) {
    return <AppWindow className={iconClass} />
  }
  if (actionLower.includes('mfa') || actionLower.includes('security') || actionLower.includes('password') || actionLower.includes('passkey')) {
    return <Shield className={iconClass} />
  }
  if (actionLower.includes('key') || actionLower.includes('api_key')) {
    return <Key className={iconClass} />
  }
  if (actionLower.includes('profile') || actionLower.includes('user')) {
    return <User className={iconClass} />
  }
  if (actionLower.includes('payment') || actionLower.includes('billing') || actionLower.includes('charge')) {
    return <CreditCard className={iconClass} />
  }
  return <Bell className={iconClass} />
}

function formatActionTitle(action: string): string {
  // Convert snake_case or camelCase to readable format
  return action
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim()
}

function groupActivityByDate(items: ActivityItem[]): GroupedActivity[] {
  const groups: Map<string, ActivityItem[]> = new Map()

  for (const item of items) {
    const date = new Date(item.created_at)
    const dateKey = date.toISOString().split('T')[0]

    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(item)
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dateKey, events]) => {
      const date = new Date(dateKey)
      let displayDate: string

      if (date.getTime() >= today.getTime()) {
        displayDate = 'Today'
      } else if (date.getTime() >= yesterday.getTime()) {
        displayDate = 'Yesterday'
      } else {
        displayDate = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      }

      return {
        date: dateKey,
        displayDate,
        events: events.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
      }
    })
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}
